import { useEffect, useState } from "react";
import { useParams, useSearchParams, Link } from "react-router-dom";
import { Plus, Trash2, Pencil, Building2, Megaphone, FileText, Link2, UserPlus, MapPin, Layers, ExternalLink, Globe } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { ws, fmtDate, empName } from "./api";
import { useWorkspace } from "./WorkspaceContext";
import { PageHeader, SubTabs, Panel, Empty, Loading, Field } from "./ui";

const TABS = [
  { key: "overview", label: "Overview", to: "/crm-app/organization/overview" },
  { key: "announcements", label: "Announcements", to: "/crm-app/organization/announcements" },
  { key: "policies", label: "Policies", to: "/crm-app/organization/policies" },
  { key: "quick-links", label: "Quick Links", to: "/crm-app/organization/quick-links" },
  { key: "new-hires", label: "New Hires", to: "/crm-app/organization/new-hires" },
];

const KINDS = {
  departments: { icon: Layers, fields: [["name", "Name"], ["description", "Description", true]], title: "name", sub: "description" },
  locations: { icon: MapPin, fields: [["name", "Name"], ["address", "Address", true]], title: "name", sub: "address" },
  announcements: { icon: Megaphone, fields: [["title", "Title"], ["body", "Message", true]], title: "title", sub: "body" },
  policies: { icon: FileText, fields: [["title", "Title"], ["body", "Policy text", true], ["url", "Link (optional, https://)"]], title: "title", sub: "body", url: true },
  "quick-links": { icon: Link2, fields: [["title", "Label"], ["url", "URL (https://)"]], title: "title", sub: "url", url: true },
};
const ID_KEY = { departments: "department_id", locations: "location_id", announcements: "announcement_id", policies: "policy_id", "quick-links": "link_id" };

const ItemDialog = ({ kind, open, onOpenChange, editing, onSaved }) => {
  const [f, setF] = useState({});
  useEffect(() => setF(editing || {}), [editing, open]);
  const save = async () => {
    try {
      const body = Object.fromEntries(KINDS[kind].fields.map(([k]) => [k, f[k] || ""]));
      if (editing) await ws(`/crm/workspace/org/${kind}/${editing[ID_KEY[kind]]}`, { method: "PATCH", body });
      else await ws(`/crm/workspace/org/${kind}`, { method: "POST", body });
      toast.success("Saved"); onOpenChange(false); onSaved();
    } catch (e) { toast.error(e.message); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent aria-describedby={undefined} data-testid={`${kind}-dialog`}>
        <DialogHeader><DialogTitle>{editing ? "Edit" : "Add"} {kind.replace("-", " ").replace(/s$/, "")}</DialogTitle></DialogHeader>
        {KINDS[kind].fields.map(([k, l, long]) => (
          <Field key={k} label={l}>{long
            ? <Textarea rows={5} value={f[k] || ""} onChange={(e) => setF({ ...f, [k]: e.target.value })} data-testid={`${kind}-${k}-input`} />
            : <Input value={f[k] || ""} onChange={(e) => setF({ ...f, [k]: e.target.value })} data-testid={`${kind}-${k}-input`} />}</Field>
        ))}
        <Button onClick={save} className="bg-[#0B1530] hover:bg-[#1a2650]" data-testid={`${kind}-save-btn`}>Save</Button>
      </DialogContent>
    </Dialog>
  );
};

export const SimpleList = ({ kind, autoOpen = false, compact = false }) => {
  const { isAdmin } = useWorkspace();
  const [items, setItems] = useState(null);
  const [open, setOpen] = useState(autoOpen && isAdmin);
  const [editing, setEditing] = useState(null);
  const cfg = KINDS[kind];
  const load = () => ws(`/crm/workspace/org/${kind}`).then((r) => setItems(r.items)).catch((e) => toast.error(e.message));
  useEffect(() => { load(); }, [kind]); // eslint-disable-line
  const del = async (it) => { if (!window.confirm("Delete this item?")) return; try { await ws(`/crm/workspace/org/${kind}/${it[ID_KEY[kind]]}`, { method: "DELETE" }); load(); } catch (e) { toast.error(e.message); } };
  const label = kind.replace("-", " ");
  return (
    <Panel title={compact ? label[0].toUpperCase() + label.slice(1) : undefined} testid={`org-list-${kind}`}
      action={compact && isAdmin && <button onClick={() => { setEditing(null); setOpen(true); }} className="text-xs text-[#0B1530] font-medium flex items-center gap-1" data-testid={`add-${kind}-btn`}><Plus className="h-3.5 w-3.5" />Add</button>}>
      {!compact && isAdmin && <div className="mb-4"><Button onClick={() => { setEditing(null); setOpen(true); }} className="bg-[#0B1530] hover:bg-[#1a2650]" data-testid={`add-${kind}-btn`}><Plus className="h-4 w-4 mr-1" />Add {label.replace(/s$/, "")}</Button></div>}
      {!items ? <Loading /> : items.length === 0 ? <Empty icon={cfg.icon} title={`No ${label} yet`} hint={isAdmin ? undefined : "Only Business Owners and Administrators can add these."} /> : (
        <ul className="divide-y divide-slate-100 -my-2">
          {items.map((it) => (
            <li key={it[ID_KEY[kind]]} className="py-3 flex gap-3" data-testid={`${kind}-item-${it[ID_KEY[kind]]}`}>
              <div className="flex-1 min-w-0">
                {cfg.url && it.url ? <a href={it.url} target="_blank" rel="noreferrer" className="text-sm font-medium text-[#0B1530] hover:underline inline-flex items-center gap-1">{it[cfg.title]}<ExternalLink className="h-3 w-3" /></a>
                  : <p className="text-sm font-medium text-slate-800">{it[cfg.title]}</p>}
                {it[cfg.sub] && cfg.sub !== "url" && <p className={`text-xs text-slate-500 mt-0.5 whitespace-pre-wrap ${compact ? "line-clamp-1" : ""}`}>{it[cfg.sub]}</p>}
                {!compact && <p className="text-[11px] text-slate-400 mt-1">{it.created_by_name} · {fmtDate(it.created_at)}</p>}
              </div>
              {isAdmin && <div className="flex gap-1 shrink-0">
                <button onClick={() => { setEditing(it); setOpen(true); }} className="p-1 text-slate-400 hover:text-[#0B1530]" data-testid={`${kind}-edit-${it[ID_KEY[kind]]}`}><Pencil className="h-3.5 w-3.5" /></button>
                <button onClick={() => del(it)} className="p-1 text-slate-400 hover:text-red-600" data-testid={`${kind}-delete-${it[ID_KEY[kind]]}`}><Trash2 className="h-3.5 w-3.5" /></button>
              </div>}
            </li>
          ))}
        </ul>
      )}
      {isAdmin && <ItemDialog kind={kind} open={open} onOpenChange={setOpen} editing={editing} onSaved={load} />}
    </Panel>
  );
};

const PROFILE_FIELDS = [["business_name", "Company name"], ["logo_url", "Logo URL"], ["website", "Website"], ["phone", "Phone"], ["industry", "Industry"], ["address", "Address", true], ["about", "About", true]];

const Profile = () => {
  const { isAdmin, refresh } = useWorkspace();
  const [d, setD] = useState(null);
  const [edit, setEdit] = useState(false);
  const [f, setF] = useState({});
  useEffect(() => { ws("/crm/workspace/organization").then(setD).catch((e) => toast.error(e.message)); }, []);
  if (!d) return <Loading />;
  const o = d.organization;
  const save = async () => {
    const body = Object.fromEntries(PROFILE_FIELDS.map(([k]) => [k, f[k] ?? ""]).filter(([k, v]) => v !== "" || o[k]));
    try { const r = await ws("/crm/workspace/organization", { method: "PATCH", body }); setD(r); setEdit(false); refresh(); toast.success("Organisation updated"); } catch (e) { toast.error(e.message); }
  };
  return (
    <Panel title="Company profile" testid="org-profile-panel" action={isAdmin && <button onClick={() => { setF(o); setEdit(true); }} className="text-xs text-[#0B1530] font-medium flex items-center gap-1" data-testid="org-edit-btn"><Pencil className="h-3.5 w-3.5" />Edit</button>}>
      <div className="flex items-start gap-4">
        {o.logo_url ? <img src={o.logo_url} alt="" className="h-16 w-16 rounded-xl object-contain border border-slate-200 bg-white" /> : <div className="h-16 w-16 rounded-xl bg-[#0B1530] text-[#E8A33D] flex items-center justify-center"><Building2 className="h-7 w-7" /></div>}
        <div className="min-w-0">
          <p className="font-heading text-xl font-semibold text-[#0B1530]" data-testid="org-name">{o.business_name}</p>
          <p className="text-sm text-slate-500">{o.industry || "Industry not set"}</p>
          {o.website && <a href={o.website} target="_blank" rel="noreferrer" className="text-sm text-[#0B1530] hover:underline inline-flex items-center gap-1 mt-1" data-testid="org-website"><Globe className="h-3.5 w-3.5" />{o.website}</a>}
        </div>
      </div>
      {o.about && <p className="text-sm text-slate-600 mt-4 whitespace-pre-wrap">{o.about}</p>}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-5">
        {[["Employees", d.counts.employees], ["CRM logins", d.counts.members], ["Departments", d.counts.departments], ["Locations", d.counts.locations]].map(([l, v]) => (
          <div key={l} className="rounded-lg bg-slate-50 p-3"><p className="text-[11px] uppercase tracking-wider text-slate-500">{l}</p><p className="font-heading text-xl font-semibold text-[#0B1530]">{v}</p></div>
        ))}
      </div>
      <div className="text-sm text-slate-600 mt-4 space-y-1">{o.address && <p>{o.address}</p>}{o.phone && <p>{o.phone}</p>}</div>
      <Dialog open={edit} onOpenChange={setEdit}>
        <DialogContent aria-describedby={undefined} className="max-h-[90vh] overflow-y-auto" data-testid="org-edit-dialog">
          <DialogHeader><DialogTitle>Edit organisation</DialogTitle></DialogHeader>
          {PROFILE_FIELDS.map(([k, l, long]) => <Field key={k} label={l}>{long
            ? <Textarea value={f[k] || ""} onChange={(e) => setF({ ...f, [k]: e.target.value })} data-testid={`org-${k}-input`} />
            : <Input value={f[k] || ""} onChange={(e) => setF({ ...f, [k]: e.target.value })} data-testid={`org-${k}-input`} />}</Field>)}
          <Button onClick={save} className="bg-[#0B1530] hover:bg-[#1a2650]" data-testid="org-save-btn">Save</Button>
        </DialogContent>
      </Dialog>
    </Panel>
  );
};

const NewHires = () => {
  const [list, setList] = useState(null);
  useEffect(() => { ws("/crm/workspace/dashboard").then((d) => setList(d.new_hires)).catch((e) => toast.error(e.message)); }, []);
  if (!list) return <Loading />;
  return (
    <Panel testid="new-hires-panel">
      {list.length === 0 ? <Empty icon={UserPlus} title="No new joiners in the last 30 days" hint="Employees with a recent joining date appear here." /> : (
        <ul className="divide-y divide-slate-100 -my-2">{list.map((e) => (
          <li key={e.employee_id} className="py-3 flex justify-between text-sm" data-testid={`new-hire-${e.employee_id}`}><span className="font-medium">{empName(e)} <span className="text-slate-400 font-normal">{e.designation}</span></span><span className="text-slate-500">Joined {fmtDate(e.joining_date)}</span></li>
        ))}</ul>
      )}
    </Panel>
  );
};

export default function OrganizationPage() {
  const { tab = "overview" } = useParams();
  const [params] = useSearchParams();
  return (
    <div data-testid="organization-page">
      <PageHeader title="Organization" subtitle="Company profile, structure and shared information" actions={<Link to="/crm-app/team"><Button variant="outline" data-testid="org-directory-btn">Employee directory</Button></Link>} />
      <SubTabs tabs={TABS} />
      {tab === "overview" && (
        <div className="grid lg:grid-cols-3 gap-4">
          <div className="lg:col-span-2"><Profile /></div>
          <div className="space-y-4"><SimpleList kind="departments" compact /><SimpleList kind="locations" compact /></div>
        </div>
      )}
      {["announcements", "policies", "quick-links"].includes(tab) && <SimpleList key={tab} kind={tab} autoOpen={!!params.get("create")} />}
      {tab === "new-hires" && <NewHires />}
    </div>
  );
}
